"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { getEffectivePassType } from "@/lib/pass-utils";
import { canUsePersonalQuiz, PERSONAL_QUIZ_GRACE_END } from "@/lib/personal-quiz/permission";

type PassMetadata = Parameters<typeof getEffectivePassType>[0];

export function usePersonalQuizAccessState() {
  const { user, isLoaded, isSignedIn } = useUser();
  const [now, setNow] = useState(Date.now);
  const metadata = (user?.publicMetadata ?? {}) as PassMetadata;
  // 공통 훅의 user 단위 memo 대신, 만료 시각에도 유효 등급을 다시 계산한다.
  const pass = getEffectivePassType(metadata);
  const allowed = !!isLoaded && !!isSignedIn && canUsePersonalQuiz(pass, now);

  useEffect(() => {
    const refresh = () => setNow(Date.now());
    const onFocus = () => {
      refresh();
      void user?.reload().catch(() => { /* API 권한 검사는 서버에서 다시 수행한다. */ });
    };
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    const deadlines = [PERSONAL_QUIZ_GRACE_END,
      Date.parse(metadata.passExpiresAt ?? ""),
      Date.parse(metadata.freeTrialExpiresAt ?? ""),
    ].filter((time) => Number.isFinite(time) && time > now);
    const timer = deadlines.length ? window.setTimeout(refresh,
      Math.min(Math.max(0, Math.min(...deadlines) - Date.now()), 2_147_483_647),
    ) : undefined;
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [now, user, metadata.passExpiresAt, metadata.freeTrialExpiresAt]);

  return { isLoaded, isSignedIn, allowed, isGrace: allowed && pass.passType !== "intelligence" };
}

export const PersonalQuizAccessContext = createContext<ReturnType<typeof usePersonalQuizAccessState> | null>(null);
export function usePersonalQuizAccess() {
  const access = useContext(PersonalQuizAccessContext);
  if (!access) throw new Error("시험지 접근 권한 공급자가 필요합니다.");
  return access;
}
